/**
 * TraceStep Component
 *
 * Collapsible step section containing activities.
 * Shows step status, description, and nested activities.
 */

import { Component, Show, For, createSignal } from 'solid-js';
import type { ExecutionEvent } from '../../../services/api.ts';
import type { AgentInfo } from '../../../stores/executionStore.ts';
import TraceActivity from './TraceActivity.tsx';

export type StepStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface TraceStepData {
  id: string;
  index: number;
  description: string;
  status: StepStatus;
  activities: ExecutionEvent[];
  durationMs?: number;
  agentId?: string;
  agentName?: string;
}

export interface TraceStepProps {
  step: TraceStepData;
  agents: AgentInfo[];
  formatTime: (timestamp: string) => string;
  formatDuration: (ms?: number) => string;
  defaultExpanded?: boolean;
}

const TraceStep: Component<TraceStepProps> = (props) => {
  // Auto-expand running steps, allow manual toggle
  const [expanded, setExpanded] = createSignal(
    props.defaultExpanded ?? props.step.status === 'running'
  );

  const getStatusIcon = () => {
    switch (props.step.status) {
      case 'completed':
        return (
          <svg class="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'running':
        return (
          <span class="relative flex h-5 w-5">
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
            <span class="relative inline-flex rounded-full h-5 w-5 bg-cyan-500 items-center justify-center">
              <span class="w-2 h-2 rounded-full bg-cyan-200" />
            </span>
          </span>
        );
      case 'failed':
        return (
          <svg class="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <div class="w-5 h-5 rounded-full border-2 border-gray-600 flex items-center justify-center">
            <span class="text-xs text-gray-500">{props.step.index + 1}</span>
          </div>
        );
    }
  };

  const getStatusBgClass = () => {
    switch (props.step.status) {
      case 'completed':
        return 'bg-green-500/5 border-green-500/20';
      case 'running':
        return 'bg-cyan-500/10 border-cyan-500/30';
      case 'failed':
        return 'bg-red-500/10 border-red-500/30';
      default:
        return 'bg-gray-800/30 border-gray-700';
    }
  };

  const toolCount = () => props.step.activities.filter(
    e => e.eventType.startsWith('tool_call')
  ).length;

  const llmCount = () => props.step.activities.filter(
    e => e.eventType.startsWith('llm_call')
  ).length;

  return (
    <div class={`rounded-lg border ${getStatusBgClass()} overflow-hidden transition-all`}>
      {/* Step header */}
      <div
        class="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-800/30 transition-colors"
        onClick={() => setExpanded(!expanded())}
      >
        {/* Expand/collapse chevron */}
        <svg
          class={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${expanded() ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>

        {/* Status icon */}
        <div class="flex-shrink-0">
          {getStatusIcon()}
        </div>

        {/* Content */}
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class={`font-medium ${
              props.step.status === 'pending' ? 'text-gray-500' :
              props.step.status === 'failed' ? 'text-red-300' :
              'text-gray-200'
            }`}>
              Step {props.step.index + 1}
            </span>

            {/* Activity counts */}
            <Show when={props.step.activities.length > 0}>
              <div class="flex items-center gap-2 text-xs">
                <Show when={toolCount() > 0}>
                  <span class="flex items-center gap-1 text-orange-400">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {toolCount()}
                  </span>
                </Show>
                <Show when={llmCount() > 0}>
                  <span class="flex items-center gap-1 text-green-400">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    {llmCount()}
                  </span>
                </Show>
              </div>
            </Show>
          </div>

          {/* Description */}
          <p class={`text-sm mt-0.5 ${
            props.step.status === 'pending' ? 'text-gray-600' : 'text-gray-400'
          }`}>
            {props.step.description}
          </p>
        </div>

        {/* Duration */}
        <div class="flex-shrink-0 text-right">
          <Show when={props.step.durationMs}>
            <span class="text-xs text-gray-500">{props.formatDuration(props.step.durationMs)}</span>
          </Show>
          <Show when={props.step.status === 'running' && !props.step.durationMs}>
            <span class="text-xs text-cyan-400">In progress</span>
          </Show>
          <Show when={props.step.status === 'pending'}>
            <span class="text-xs text-gray-600">Pending</span>
          </Show>
        </div>
      </div>

      {/* Activities */}
      <Show when={expanded() && props.step.activities.length > 0}>
        <div class="border-t border-gray-700/50 px-4 py-2 space-y-1">
          <For each={props.step.activities}>
            {(event) => (
              <TraceActivity
                event={event}
                agents={props.agents}
                formatTime={props.formatTime}
                formatDuration={props.formatDuration}
              />
            )}
          </For>
        </div>
      </Show>

      {/* Empty state for running steps */}
      <Show when={expanded() && props.step.activities.length === 0 && props.step.status === 'running'}>
        <div class="border-t border-gray-700/50 px-4 py-6 text-center">
          <div class="flex items-center justify-center gap-2 text-cyan-400">
            <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span class="text-sm">Processing...</span>
          </div>
        </div>
      </Show>
    </div>
  );
};

export default TraceStep;
